import React, {type ChangeEvent, type JSX} from 'react';
import {useMaskito} from '@maskito/react';
import {Box, FormControl, OutlinedInput} from '@mui/material';
import {Grid} from '@mui/system';
import {useFormikContext} from 'formik';
import {replaceToNumbers} from '../constants/patterns';
import {type FormValues} from '../constants/types';
import {
  BlockHeader,
  InputFormControlWithFormik,
  InputHelperText,
  InputLabel,
} from './shared';

function UserInformationSubForm(): JSX.Element {
  const {values, setFieldTouched, errors, setFieldValue, touched} =
    useFormikContext<FormValues>();

  const handleOnBlur = () => {
    if (values.userPhoneNumber) {
      setFieldTouched('userPhoneNumber', true);
    } else {
      setFieldTouched('userPhoneNumber', false);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const unMaskPhoneNumber = event.target.value.replace(replaceToNumbers, '');
    setFieldValue('userPhoneNumber', unMaskPhoneNumber);
  };

  const maskOptions = {
    mask: [
      '(',
      /\d/,
      /\d/,
      /\d/,
      ')',
      ' ',
      /\d/,
      /\d/,
      /\d/,
      '-',
      /\d/,
      /\d/,
      /\d/,
      /\d/,
    ],
  };

  const maskedInputRef = useMaskito({options: maskOptions});

  return (
    <Box mb={5} data-testid="user-information-block">
      <BlockHeader
        title="Your Information"
        subtitle="Please provide your contact information. We may need to contact you about this request."
      />
      <Grid container spacing={2}>
        <Grid size={4}>
          <InputFormControlWithFormik
            fullWidth
            required
            id="userFirstName"
            name="userFirstName"
            label="First Name"
            testIdPrefix="user-first-name"
            error={(touched.userFirstName && errors.userFirstName) || ''}
          />
        </Grid>
        <Grid size={4}>
          <InputFormControlWithFormik
            fullWidth
            required
            id="userLastName"
            name="userLastName"
            label="Last Name"
            testIdPrefix="user-last-name"
            error={(touched.userLastName && errors.userLastName) || ''}
          />
        </Grid>
        <Grid size={4}>
          <InputFormControlWithFormik
            fullWidth
            required
            id="userEmail"
            name="userEmail"
            label="Email"
            testIdPrefix="user-email"
            error={(touched.userEmail && errors.userEmail) || ''}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={4}>
          <FormControl
            fullWidth
            error={Boolean(
              (errors.userPhoneNumber &&
                touched.userPhoneNumber &&
                values.userPhoneNumber.length > 0) ||
              ''
            )}
          >
            <InputLabel
              data-testid="phone-number-label"
              htmlFor="userPhoneNumber"
            >
              Phone number (Optional)
            </InputLabel>
            <OutlinedInput
              id="userPhoneNumber"
              name="userPhoneNumber"
              placeholder="(XXX) XXX-XXXX"
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              inputProps={{
                'data-testid': 'phone-input',
                ref: maskedInputRef,
              }}
            />
            <InputHelperText data-testid="phone-number-input-helper" shrink>
              {touched.userPhoneNumber && values.userPhoneNumber
                ? errors.userPhoneNumber
                : ''}
            </InputHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UserInformationSubForm;
